import dayjs from "dayjs";
import { useFormik } from "formik";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { SignUpContainer } from "./components/SignUpContainer";
import { Card } from "./components/Card";
import {
  useFetchDepartmentsQuery,
  useFetchGroupedEquipmentsQuery,
  useFetchTeamsQuery,
  useFetchUserForUpdateQuery,
  usePostUserMutation,
} from "../../store";
import Loading from "../../components/shared/Loading";
import CustomSelect from "../../components/shared/CustomSelect";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import Select from "@mui/material/Select";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import ListSubheader from "@mui/material/ListSubheader";
import { useDispatch } from "react-redux";
import {
  openErrorToast,
  openSuccessToast,
} from "../../store/slices/toast/toastSlice";
import CustomDatePicker from "../../components/shared/CustomDatePicker";
import validations from "./validations/editPersonelValidations";
import FormHelperText from "@mui/material/FormHelperText";
import UpdateIcon from "@mui/icons-material/Update";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import GroupsIcon from "@mui/icons-material/Groups";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import { useParams } from "react-router-dom";

const EditPersonel = () => {
  let { id } = useParams();
  const dispatch = useDispatch();
  const { data: userData, isLoading: userLoading } =
    useFetchUserForUpdateQuery(id);
  const { data: departments, isLoading: departmentsLoading } =
    useFetchDepartmentsQuery();
  const { data: teamList, isLoading: teamLoading } = useFetchTeamsQuery();
  const { data: equipments, isLoading: equipmentLoading } =
    useFetchGroupedEquipmentsQuery();
  const [createUser, { isLoading: createUserLoading }] = usePostUserMutation();
  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };
  function getStyles(name, personName, theme) {
    return {
      fontWeight: personName.includes(name)
        ? theme.typography.fontWeightMedium
        : theme.typography.fontWeightRegular,
    };
  }
  const formik = useFormik({
    initialValues: {
      id: id,
      email: userData?.data.email,
      firstName: userData?.data.name,
      lastName: userData?.data.lastName,
      departmentId: userData?.data.departmentId ?? 0,
      isManager: false,
      isIntern: userData?.data.isIntern ?? false,
      workingStartDate: userData?.data.workingStartDate,
      equipments: userData?.data.equipments || [],
      teams: userData?.data.teams || [],
    },
    enableReinitialize: true,
    validationSchema: validations,
    onSubmit: async (values, bag) => {
      try {
        const model = {
          id: values.id,
          name: values.firstName,
          lastName: values.lastName,
          email: values.email,
          isManager: false,
          isIntern: values.isIntern,
          departmentId: values.departmentId,
          equipments: values.equipments,
          workingStartDate:
            values.workingStartDate !== null
              ? dayjs(values.workingStartDate).format("YYYY-MM-DD")
              : null,
          teams: values.teams,
        };
        createUser(model).then((res) => {
          if (res) {
            if (!res.data.isSuccess) {
              dispatch(openErrorToast(res.data.message));
            } else {
              dispatch(openSuccessToast("Kullanıcı Güncellendi"));
            }
          }
        });
      } catch (error) {
        bag.setErrors({ general: error.response.data.message });
      }
    },
  });

  const handleTeamChange = (event) => {
    const {
      target: { value },
    } = event;
    formik.setFieldValue("teams", value);
  };
  const handleEquipmentChange = (event) => {
    const {
      target: { value },
    } = event;
    formik.setFieldValue("equipments", value);
  };
  const handleChangeDate = ({ name, value }) => {
    const day = new Date(value).getDay();
    if (day === 6 || day === 0) {
      dispatch(openErrorToast("Hafta İçi Seçim Yapınız !"));
      formik.setFieldValue(name, dayjs(null).format("YYYY-MM-DD"));
    } else {
      var date = value ? dayjs(value).format("YYYY-MM-DD") : null;
      formik.setFieldValue(name, date);
    }
  };
  const theme = useTheme();
  if (
    departmentsLoading ||
    teamLoading ||
    equipmentLoading ||
    createUserLoading ||
    userLoading
  )
    return (
      <Loading
        isLoading={
          departmentsLoading ||
          teamLoading ||
          equipmentLoading ||
          createUserLoading ||
          userLoading
        }
      />
    );
  return (
    <>
      <CssBaseline enableColorScheme />
      <SignUpContainer direction="row" justifyContent="space-between" mb={4}>
        <Card
          variant="outlined"
          sx={{
            maxWidth: 500,
            mx: "auto",
            mt: { xs: 6, sm: 8 },
            p: { xs: 2, sm: 4 },
            borderRadius: 4,
            boxShadow: 6,
            bgcolor: "#f8fafc",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 2,
              justifyContent: "center",
            }}
          >
            <UpdateIcon sx={{ fontSize: 36, color: "primary.main" }} />
            <Typography
              component="h1"
              variant="h4"
              sx={{
                fontWeight: 800,
                color: "primary.main",
                letterSpacing: 1,
                textShadow: "0 2px 8px rgba(0,0,0,0.08)",
                fontSize: "clamp(2rem, 10vw, 2.15rem)",
              }}
            >
              Kullanıcı Güncelle
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <form onSubmit={formik.handleSubmit}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <FormControl>
                <TextField
                  autoComplete="firstName"
                  name="firstName"
                  fullWidth
                  id="firstName"
                  placeholder="Ad"
                  label="Ad"
                  value={formik.values.firstName}
                  error={
                    formik.touched.firstName && Boolean(formik.errors.firstName)
                  }
                  onChange={formik.handleChange}
                  helperText={
                    formik.touched.firstName && formik.errors.firstName
                  }
                  color={
                    formik.touched.firstName && formik.errors.firstName
                      ? "error"
                      : "primary"
                  }
                  InputProps={{
                    startAdornment: (
                      <AssignmentIndIcon sx={{ mr: 1, color: "grey.500" }} />
                    ),
                  }}
                />
              </FormControl>
              <FormControl>
                <TextField
                  fullWidth
                  id="lastName"
                  placeholder="Soyad"
                  name="lastName"
                  label="Soyad"
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  autoComplete="lastName"
                  error={
                    formik.touched.lastName && Boolean(formik.errors.lastName)
                  }
                  helperText={formik.touched.lastName && formik.errors.lastName}
                  color={
                    formik.touched.lastName && formik.errors.lastName
                      ? "error"
                      : "primary"
                  }
                  InputProps={{
                    startAdornment: (
                      <AssignmentIndIcon sx={{ mr: 1, color: "grey.500" }} />
                    ),
                  }}
                />
              </FormControl>
              <FormControl>
                <TextField
                  fullWidth
                  name="email"
                  placeholder="ad.soyad@wtechin.com"
                  type="email"
                  id="email"
                  label="E-Posta"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  autoComplete="email"
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  color={
                    formik.touched.email && formik.errors.email
                      ? "error"
                      : "primary"
                  }
                />
              </FormControl>
              <FormControl>
                <CustomDatePicker
                  handleChange={handleChangeDate}
                  value={formik.values.workingStartDate}
                  id="workingStartDate"
                  label={"İşe Başlama Tarihi"}
                />
              </FormControl>
              <FormControl
                error={
                  formik.touched.departmentId &&
                  Boolean(formik.errors.departmentId)
                }
              >
                <InputLabel htmlFor="departmentId-label">Departman</InputLabel>
                <CustomSelect
                  data={departments?.data}
                  handleChange={formik.handleChange}
                  id="departmentId"
                  value={formik.values.departmentId}
                />
                {formik.touched.departmentId && (
                  <FormHelperText>{formik.errors.departmentId}</FormHelperText>
                )}
              </FormControl>
              <FormControl>
                <InputLabel id="team-label">Takım</InputLabel>
                <Select
                  labelId="team-label"
                  id="teams"
                  multiple
                  fullWidth
                  value={formik.values.teams}
                  onChange={handleTeamChange}
                  input={
                    <OutlinedInput id="select-multiple-chip" label="Takım" />
                  }
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((id) => {
                        const team = teamList?.data.find((t) => t.id === id);
                        return (
                          <Chip
                            key={id}
                            label={team ? team.name : id}
                            icon={<GroupsIcon sx={{ fontSize: 18 }} />}
                          />
                        );
                      })}
                    </Box>
                  )}
                  MenuProps={MenuProps}
                >
                  {teamList?.data.map((name) => (
                    <MenuItem
                      key={name.id}
                      value={name.id}
                      style={getStyles(name.name, formik.values.teams, theme)}
                    >
                      {name.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <InputLabel id="equipments-label">Zimmetler</InputLabel>
                <Select
                  labelId="equipments-label"
                  id="equipments"
                  multiple
                  fullWidth
                  value={formik.values.equipments}
                  onChange={handleEquipmentChange}
                  input={
                    <OutlinedInput
                      id="select-multiple-chip-equipments"
                      label="Zimmetler"
                    />
                  }
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((id) => {
                        // Grup içinde ilgili ekipmanı bul
                        let equipmentName = id;
                        equipments?.data.forEach((group) => {
                          const eq = group.equipments.find(
                            (e) => e.value === id
                          );
                          if (eq) equipmentName = eq.text;
                        });
                        return (
                          <Chip
                            key={id}
                            label={equipmentName}
                            icon={<Inventory2Icon sx={{ fontSize: 18 }} />}
                          />
                        );
                      })}
                    </Box>
                  )}
                  MenuProps={MenuProps}
                >
                  {equipments?.data.map((group) => [
                    <ListSubheader key={group.groupName}>
                      {group.groupName}
                    </ListSubheader>,
                    group.equipments.map((equipment) => (
                      <MenuItem
                        key={equipment.value}
                        value={equipment.value}
                        style={getStyles(
                          equipment.text,
                          formik.values.equipments,
                          theme
                        )}
                      >
                        {equipment.text}
                      </MenuItem>
                    )),
                  ])}
                </Select>
              </FormControl>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  mt: 2,
                  fontWeight: 700,
                  borderRadius: 3,
                  py: 1.2,
                  fontSize: "1.1rem",
                  letterSpacing: 0.5,
                  boxShadow: 2,
                }}
                startIcon={<UpdateIcon />}
              >
                Kaydet
              </Button>
            </Box>
          </form>
        </Card>
      </SignUpContainer>
    </>
  );
};

export default EditPersonel;
